/**
 * Componente AppointmentSummary
 * Resumen post-cita: modo lectura o edición inline.
 * Reutilizable en detalle de cita, modal rápido y panel del calendario.
 * @module components/appointments/AppointmentSummary
 */

import { useState } from 'react';
import { PencilSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const MIN_LENGTH = 50;
const MAX_LENGTH = 500;
const PLACEHOLDER =
  'Escribe un resumen de los puntos clave tratados en la cita...';

export interface AppointmentSummaryProps {
  appointmentId: string;
  summary: string | null;
  /** Si la cita está completada, se puede editar el resumen */
  status: string | null | undefined;
  onSave: (summary: string) => Promise<void>;
  /** Mostrar borde de alerta cuando está completada y sin resumen */
  showPendingAlert?: boolean;
  className?: string;
}

export function AppointmentSummary({
  appointmentId: _appointmentId,
  summary,
  status,
  onSave,
  showPendingAlert = true,
  className = '',
}: AppointmentSummaryProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(summary ?? '');
  const [saving, setSaving] = useState(false);

  const isCompleted = status === 'completed';
  const hasSummary = Boolean(summary?.trim());
  const needsSummary = isCompleted && !hasSummary;

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed.length < MIN_LENGTH) {
      toast.error(`El resumen debe tener al menos ${MIN_LENGTH} caracteres.`);
      return;
    }
    if (trimmed.length > MAX_LENGTH) {
      toast.error(`El resumen no puede superar ${MAX_LENGTH} caracteres.`);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
      toast.success('Resumen guardado correctamente.');
    } catch (err) {
      console.error('Error guardando resumen:', err);
      toast.error('No se pudo guardar el resumen. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (!isCompleted) {
    return (
      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 ${className}`}
      >
        El resumen estará disponible cuando la cita se marque como completada.
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={`space-y-3 ${className}`}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={5}
          className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-y text-sm"
          aria-label="Resumen de la cita"
        />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-gray-500">
            {draft.length} / {MAX_LENGTH} caracteres (mín. {MIN_LENGTH})
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setDraft(summary ?? '');
              }}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving || draft.trim().length < MIN_LENGTH}
              className="min-h-[44px]"
            >
              {saving ? (
                'Guardando...'
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-1.5" />
                  Guardar resumen
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border p-4 ${
        needsSummary && showPendingAlert
          ? 'border-amber-400 bg-amber-50/80'
          : 'border-gray-200 bg-white'
      } ${className}`}
    >
      {hasSummary ? (
        <>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{summary}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-3 text-primary hover:bg-primary/10 min-h-[44px]"
            onClick={() => {
              setDraft(summary ?? '');
              setIsEditing(true);
            }}
          >
            <PencilSquareIcon className="w-4 h-4 mr-1.5" />
            Editar resumen
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-500 italic mb-3">Sin resumen aún.</p>
          <Button
            type="button"
            size="sm"
            className="min-h-[44px] bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => {
              setDraft('');
              setIsEditing(true);
            }}
          >
            <PencilSquareIcon className="w-4 h-4 mr-1.5" />
            Agregar resumen
          </Button>
        </>
      )}
    </div>
  );
}
